import { Config } from '@/lib/config';
import type { paths } from '@coffice/shared';
import createClient, { Middleware } from 'openapi-fetch';

let getAccessToken: (() => Promise<string | null>) | null = null;

export const setAuthTokenGetter = (
  tokenGetter: () => Promise<string | null>,
) => {
  getAccessToken = tokenGetter;
};

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    if (getAccessToken) {
      try {
        const token = await getAccessToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        console.error('Failed to get access token:', error);
      }
    }
    return request;
  },
};

// Cache control middleware - disable HTTP caching for React Query
const cacheControlMiddleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
    request.headers.set('Pragma', 'no-cache');
    return request;
  },
};

// Error handling middleware
const errorHandlingMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 401) {
        // Handle unauthorized - will be handled by Auth0 provider
        console.warn('Unauthorized request, user may need to re-authenticate');
        return response;
      }

      try {
        const errorData = await response.json();
        const errorMessage =
          errorData.message || `API Error ${response.status}`;
        throw new Error(errorMessage);
      } catch (jsonError) {
        // If response isn't JSON, use status-based error
        const errorMessage =
          response.status === 404
            ? 'Resource not found'
            : `Network error ${response.status}`;
        throw new Error(errorMessage);
      }
    }

    return response;
  },
};

// Create API client with middleware
const apiClient = createClient<paths>({
  baseUrl: Config.apiUrl,
});

apiClient.use(cacheControlMiddleware);
apiClient.use(authMiddleware);
apiClient.use(errorHandlingMiddleware);

// Export the configured client
export default apiClient;
export { apiClient };
